<template>
  <div class="max-metric max-panel" :data-intent="intent ?? undefined">
    <p class="max-metric__label">{{ node.label }}</p>
    <p class="max-metric__value">{{ displayValue }}</p>
    <p v-if="node.delta" class="max-metric__delta" :data-intent="deltaIntent">
      <svg v-if="node.delta.value !== 0" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" :style="{ transform: node.delta.value < 0 ? 'rotate(180deg)' : undefined }">
        <path d="M5 1.5 9 7.5H1z" fill="currentColor" />
      </svg>
      <span>{{ deltaText }}</span>
      <span v-if="node.delta.label" class="max-metric__caption">{{ node.delta.label }}</span>
    </p>
    <svg
      v-if="spark"
      class="max-metric__spark"
      :viewBox="`0 0 ${spark.w} ${spark.h}`"
      preserveAspectRatio="none"
      aria-hidden="true"
      style="width:100%;height:36px;margin-top:4px"
    >
      <path :d="spark.area" :fill="`color-mix(in srgb, var(--max-intent, var(--max-color-accent)) 14%, transparent)`" />
      <path :d="spark.line" fill="none" stroke="var(--max-intent, var(--max-color-accent))" stroke-width="2" vector-effect="non-scaling-stroke" stroke-linejoin="round" stroke-linecap="round" />
    </svg>
    <p v-if="node.caption" class="max-metric__caption">{{ node.caption }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { formatValue, formatDelta } from '../core/format.ts'
import { evaluateRules } from '../core/rules.ts'
import { useMaxContext } from '../runtime/context.ts'

const props = defineProps<{ node: any }>()
const ctx = useMaxContext()

const shown = ref<number | null>(null)
const countUp = computed(() => props.node.motion === 'count-up' && typeof props.node.value === 'number' && !ctx.reducedMotion.value)

function animate (to: number) {
  const from = 0
  const duration = 900
  const start = performance.now()
  const step = (now: number) => {
    const t = Math.min(1, (now - start) / duration)
    const eased = 1 - Math.pow(1 - t, 3)
    shown.value = from + (to - from) * eased
    if (t < 1) requestAnimationFrame(step)
  }
  requestAnimationFrame(step)
}

onMounted(() => { if (countUp.value) animate(props.node.value) })
watch(() => props.node.value, (v) => { if (countUp.value) animate(v) })

const displayValue = computed(() => {
  const value = countUp.value && shown.value !== null ? shown.value : props.node.value
  if (typeof value === 'string') return value
  return formatValue(value, { format: props.node.format, currency: props.node.currency, locale: ctx.locale, compact: Math.abs(Number(value)) >= 1e7 })
})

const intent = computed(() => props.node.intent ?? evaluateRules(props.node.rules, { value: props.node.value })?.intent ?? null)

const deltaIntent = computed(() => {
  const d = props.node.delta
  if (!d || d.value === 0) return 'neutral'
  const good = (d.goodDirection ?? 'up') === 'up' ? d.value > 0 : d.value < 0
  return good ? 'positive' : 'negative'
})

const deltaText = computed(() => {
  const d = props.node.delta
  return formatDelta(d.value, { format: d.format ?? 'percent', locale: ctx.locale, currency: props.node.currency })
})

const spark = computed(() => {
  const values = (props.node.sparkline ?? []).filter((v: unknown) => typeof v === 'number') as number[]
  if (values.length < 2) return null
  const w = 100
  const h = 30
  const min = Math.min(...values)
  const max = Math.max(...values)
  const range = max - min || 1
  const pts = values.map((v, i) => [i / (values.length - 1) * w, h - 2 - (v - min) / range * (h - 4)])
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ')
  return { w, h, line, area: `${line} L${w},${h} L0,${h} Z` }
})
</script>
